import React, { useState, useEffect } from 'react';
import './Statistics.css';
import { useAuth } from '../../Security/AuthProvider';
import { useNotification } from '../../Utils/Commons/Notification/NotificationContext';
// OptimizedAPI supprimé
import { SmartMemo, useExpensiveComputation, useRenderTracker } from '../../Utils/ComponentOptimizer';

const Statistics = () => {
  const { getCredentials } = useAuth();
  const credentials = getCredentials();
  const [loading, setLoading] = useState(true);
  
  // Tracking des renders pour optimisation
  useRenderTracker('Statistics', { loading });
  const [statsData, setStatsData] = useState({
    globalStats: {},
    agentStats: [],
    operationStats: [],
    monthlyTrends: []
  });
  const { notifyError, notifySuccess } = useNotification();
  
  // État pour la popup des détails d'agent
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      console.log('📊 [Statistics] Chargement avec cache intelligent...');

      // OptimizedAPI supprimé - utiliser fetch directement
      const data = await fetch('/statistics/company-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials?.company_name
        }),
      }).then(res => res.json());
      
      if (data.success) {
        setStatsData(data.data);
        console.log('✅ [Statistics] Statistiques chargées depuis le cache/serveur');
        notifySuccess('Statistiques chargées avec succès');
      } else {
        notifyError(data.error || 'Erreur lors du chargement des statistiques');
      }
    } catch (error) {
      console.error("❌ [Statistics] Erreur optimisée:", error);
      
      if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
        notifyError('Erreur de connexion au serveur. Vérifiez que le serveur est démarré.');
      } else {
        notifyError(`Erreur: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction de test temporaire
  const fetchTestStatistics = async () => {
    try {
      setLoading(true);

      const response = await fetch('/statistics/test-operations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials?.company_name
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setStatsData(data.data);
        console.log('📊 Données de test chargées:', data.data);
      } else {
        notifyError(data.error || 'Erreur lors du chargement des statistiques de test');
      }
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques de test:", error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        notifyError('Erreur de connexion au serveur de test. Vérifiez que le serveur est démarré.');
      } else if (error.message.includes('NetworkError')) {
        notifyError('Erreur réseau lors du test. Vérifiez votre connexion internet.');
      } else {
        notifyError(`Erreur de test: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les détails de trajet d'un agent
  const fetchAgentTravelDetails = async (agent) => {
    try {
      setDetailsLoading(true);
      setSelectedAgent(agent);

      const response = await fetch('/statistics/agent-travel-details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: credentials?.company_name,
          agent_id: agent.id
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setAgentDetails(data.data);
        notifySuccess('Détails de trajet chargés avec succès');
      } else {
        notifyError(data.error || 'Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error("Erreur lors du chargement des détails de trajet:", error);
      notifyError(`Erreur: ${error.message}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fonction pour fermer la popup
  const closeAgentDetails = () => {
    setSelectedAgent(null);
    setAgentDetails(null);
  };

  if (loading) {
    return (
      <div className="statistics-container">
        <div className="loading-section">
          <div className="loading-spinner"></div>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-container">
      <div className="statistics-dashboard">
        
        <header className="dashboard-header">
          <h1>Statistiques</h1>
        </header>

        <main className="main-content">
          {/* Performance des Agents */}
          <section className="stats-section">
            <h2>👥 Performance des Agents</h2>
            <div className="agent-stats-table-container">
              <table className="agent-stats-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>RDV total</th>
                    <th>RDV ce mois</th>
                    <th>RDV complétés</th>
                    <th>Taux réussite</th>
                  </tr>
                </thead>
                <tbody>
                  {statsData.agentStats && statsData.agentStats.length > 0 ? (
                    statsData.agentStats.map((agent, index) => (
                      <tr key={index} onClick={() => agent.hasAddressData && fetchAgentTravelDetails(agent)}>
                        <td className="agent-name">{agent.name}</td>
                        <td>{agent.totalRdv}</td>
                        <td>{agent.monthlyRdv}</td>
                        <td>{agent.completedRdv}</td>
                        <td>
                          <span className={`completion-rate ${agent.completionRate > 80 ? 'excellent' : agent.completionRate > 60 ? 'good' : 'average'}`}>
                            {agent.completionRate}%
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                        Aucun agent trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Opérations les plus demandées */}
          <section className="stats-section">
            <h2>🔧 Opérations les plus demandées</h2>
            <div className="operations-grid">
              {statsData.operationStats && statsData.operationStats.length > 0 ? (
                statsData.operationStats.map((operation, index) => (
                  <div key={index} className="operation-card">
                    <div className="operation-rank">#{index + 1}</div>
                    <div className="operation-details">
                      <h4>{operation.name}</h4>
                      <div className="operation-metrics">
                        <span className="metric">
                          <strong>{operation.usageCount}</strong> utilisations
                        </span>
                        <span className="metric">
                          <strong>{operation.duration * 15}min</strong>
                        </span>
                        <span className="metric">
                          <strong>{operation.successRate}%</strong> succès
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>Aucune opération trouvée</p>
              )}
            </div>
          </section>
        </main>

        <aside className="sidebar">
          {/* Vue d'ensemble */}
          <section className="stats-section">
            <h2>📈 Vue d'ensemble</h2>
            <div className="global-stats-list">
              <div className="global-stat-item">
                <span className="stat-label">🎯 RDV total</span>
                <span className="stat-value">{statsData.globalStats.totalRdv || 0}</span>
              </div>
              <div className="global-stat-item">
                <span className="stat-label">📅 RDV ce mois</span>
                <span className="stat-value">{statsData.globalStats.monthlyRdv || 0}</span>
              </div>
              <div className="global-stat-item">
                <span className="stat-label">✅ Taux de réalisation</span>
                <span className="stat-value">{statsData.globalStats.completionRate || 0}%</span>
              </div>
              <div className="global-stat-item">
                <span className="stat-label">👥 Clients total</span>
                <span className="stat-value">
                  {statsData.globalStats.totalClients || 0}
                  {statsData.globalStats.totalClients === 1 ? ' client' : ' clients'}
                </span>
              </div>
            </div>
          </section>

          {/* Tendances mensuelles */}
          {statsData.monthlyTrends && statsData.monthlyTrends.length > 0 && (
            <section className="stats-section">
              <h2>📊 Évolution des Rendez-vous</h2>
              <div className="trends-chart">
                {(() => {
                  const maxRdv = Math.max(...statsData.monthlyTrends.map(t => t.rdvCount), 0);
                  return statsData.monthlyTrends.map((trend, index) => (
                    <div key={index} className="trend-bar-wrapper" title={`${trend.rdvCount} RDV, ${trend.completionRate}% de réussite en ${trend.month}`}>
                      <span className="bar-value">{trend.rdvCount}</span>
                      <div className="trend-bar">
                        <div 
                          className="trend-bar-fill" 
                          style={{ height: maxRdv > 0 ? `${(trend.rdvCount / maxRdv) * 100}%` : '0%' }}
                        ></div>
                      </div>
                      <span className="bar-label">{trend.month.substring(0, 3)}.</span>
                      <span className="bar-sub-label">{trend.completionRate}%</span>
                    </div>
                  ));
                })()}
              </div>
            </section>
          )}
        </aside>

      </div>

      {/* Popup détails agent */}
      {selectedAgent && (
        <div className="agent-details-popup">
          <div className="popup-overlay" onClick={closeAgentDetails}></div>
          <div className="popup-content">
            <div className="popup-header">
              <h3>🚗 Analyse des trajets - {selectedAgent.name}</h3>
              <button className="close-button" onClick={closeAgentDetails}>✖</button>
            </div>
            
            {detailsLoading ? (
              <div className="popup-loading">
                <div className="loading-spinner"></div>
                <p>Calcul des trajets en cours...</p>
              </div>
            ) : agentDetails ? (
              <div className="popup-body">
                {/* Résumé des trajets */}
                <div className="travel-summary">
                  <div className="summary-cards">
                    <div className="summary-card primary">
                      <div className="card-icon">⏱️</div>
                      <div className="card-content">
                        <h4>{agentDetails.averageTravelTime || 'N/A'} min</h4>
                        <p>Temps moyen</p>
                      </div>
                    </div>
                    <div className="summary-card success">
                      <div className="card-icon">📊</div>
                      <div className="card-content">
                        <h4>{agentDetails.validTravelTimes}</h4>
                        <p>RDV analysés</p>
                      </div>
                    </div>
                    <div className={`summary-card efficiency-${agentDetails.travelEfficiency.toLowerCase()}`}>
                      <div className="card-icon">🎯</div>
                      <div className="card-content">
                        <h4>{agentDetails.travelEfficiency}</h4>
                        <p>Efficacité</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistiques détaillées */}
                <div className="travel-details">
                  <h4>📍 Adresse de base : {agentDetails.agentAddress}</h4>
                  
                  <div className="travel-stats">
                    <div className="stat-item">
                      <span className="stat-label">Trajet le plus court:</span>
                      <span className="stat-value">{agentDetails.summary.shortestTravel} min</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Trajet le plus long:</span>
                      <span className="stat-value">{agentDetails.summary.longestTravel} min</span>
                    </div>
                  </div>
                </div>

                {/* Liste des RDV */}
                <div className="rdv-list">
                  <h4>📅 RDV du mois avec trajets calculés</h4>
                  <div className="rdv-table">
                    <div className="rdv-header">
                      <div>Date</div>
                      <div>Heure</div>
                      <div>Client</div>
                      <div>Adresse</div>
                      <div>Trajet</div>
                    </div>
                    {agentDetails.rdvDetails.map((rdv, index) => (
                      <div key={index} className="rdv-row">
                        <div className="rdv-date">{rdv.date}</div>
                        <div className="rdv-time">{rdv.time}</div>
                        <div className="rdv-client">{rdv.clientName}</div>
                        <div className="rdv-address">{rdv.address}</div>
                        <div className={`rdv-travel ${rdv.travelTime <= 20 ? 'excellent' : rdv.travelTime <= 30 ? 'good' : rdv.travelTime <= 40 ? 'average' : 'poor'}`}>
                          {rdv.travelTimeFormatted}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="popup-error">
                <p>Erreur lors du chargement des détails</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartMemo(Statistics);